/*
 * OPS-05 · the marketing half of "จัดซื้อและการตลาด".
 *
 * The number this panel exists to state carefully is ROAS: revenue JamTick already owns, divided by
 * a figure an operator typed in. It is exactly as trustworthy as that figure, and three things
 * follow from that — none of them decoration:
 *
 * - **Unknown is not zero.** A campaign nobody has filled in reads "ยังไม่ได้กรอก", never ฿0, and
 *   clearing the field puts it back to unknown rather than recording a spend of nothing.
 * - **A half-filled register flatters itself.** The server divides the event's *whole* paid revenue
 *   by the sum of only the campaigns that have a figure, so ROAS climbs as campaigns are left
 *   blank. The old panel printed that number with nothing beside it; this one says how many
 *   campaigns are missing and that the figure is therefore too high.
 * - **Where the figure came from is part of the figure.** `spendSource` has been in the API and on
 *   the old screen's table since TICK-125, read-only — displayed and never settable, so the one
 *   thing that makes a hand-typed number auditable could not be recorded. It is a field here.
 */
import { useState } from 'react';
import { Alert, Button, Card, ConfirmDialog, KpiCard, KpiGrid, TextField } from '../..';
import type { OpsCampaignDto } from '../../../api';
import type { OpsMarketingState } from './useOpsMarketing';

const baht = (n: number) => `฿${Math.round(n).toLocaleString('th-TH')}`;

/** The one place "we do not know" is rendered, so it cannot drift into looking like a number. */
const UNKNOWN = 'ยังไม่มีข้อมูล';

export function MarketingPanel({ state }: { state: OpsMarketingState }) {
  const { campaigns, summary, coverage, loading, loadError, error, busy } = state;

  const [name, setName] = useState('');
  const [phase, setPhase] = useState('');
  const [target, setTarget] = useState('');
  const [adding, setAdding] = useState(false);

  const [editing, setEditing] = useState<OpsCampaignDto | null>(null);
  const [spend, setSpend] = useState('');
  const [source, setSource] = useState('');
  const [removing, setRemoving] = useState<OpsCampaignDto | null>(null);

  const openEditor = (c: OpsCampaignDto) => {
    setEditing(c);
    setSpend(c.actualAdsSpend === null ? '' : String(c.actualAdsSpend));
    setSource(c.spendSource ?? '');
  };

  return (
    <Card
      title={`การตลาด · ${campaigns.length} แคมเปญ`}
      sub="รายได้มาจากออเดอร์จริงที่ระบบเป็นเจ้าของ ค่าโฆษณาเป็นตัวเลขที่กรอกเอง"
      action={
        !adding && (
          <Button size="sm" variant="secondary" onClick={() => setAdding(true)}>＋ เพิ่มแคมเปญ</Button>
        )
      }
    >
      {loading && <p className="jt-field-hint">กำลังโหลด…</p>}
      {loadError && <Alert tone="danger" title="โหลดไม่สำเร็จ">{loadError}</Alert>}
      {error && <Alert tone="danger" title="ทำรายการไม่สำเร็จ">{error}</Alert>}

      {summary && (
        <>
          <KpiGrid>
            <KpiCard label="งบโฆษณาที่ตั้งไว้" value={baht(summary.targetAdsSpend)} />
            <KpiCard
              label="ใช้จริง"
              value={summary.actualAdsSpend === null ? UNKNOWN : baht(summary.actualAdsSpend)}
              meta={coverage.total > 0 ? `กรอกแล้ว ${coverage.reported} จาก ${coverage.total} แคมเปญ` : undefined}
            />
            <KpiCard label="รายได้ที่ระบบบันทึก" value={baht(summary.attributedRevenue)} />
            <KpiCard
              label="ROAS"
              value={summary.roas === null ? UNKNOWN : `${summary.roas.toFixed(2)}×`}
              tone={coverage.partial ? 'warning' : undefined}
              meta={coverage.partial ? 'สูงกว่าความจริง — ดูคำอธิบายด้านล่าง' : undefined}
            />
          </KpiGrid>

          {/*
            * The warning the old screen never gave. Revenue is whole; the denominator is partial;
            * the quotient is therefore flattering, and by how much nobody can say.
            */}
          {coverage.partial && (
            <Alert tone="warning" title={`${coverage.missing} แคมเปญยังไม่ได้กรอกค่าโฆษณา`}>
              ROAS ข้างบนเอารายได้ <strong>ทั้งงาน</strong> มาหารด้วยค่าโฆษณาของ{' '}
              {coverage.reported} แคมเปญเท่านั้น — ตัวเลขจึงสูงกว่าความจริง และจะลดลงเมื่อกรอกครบ
            </Alert>
          )}
          {coverage.total > 0 && coverage.reported === 0 && (
            <Alert tone="info" title="ยังไม่มีใครกรอกค่าโฆษณาที่ใช้จริง">
              ROAS คำนวณไม่ได้จนกว่าจะมีตัวเลขอย่างน้อยหนึ่งแคมเปญ — ช่องว่างคือ &ldquo;ยังไม่รู้&rdquo; ไม่ใช่ ฿0
            </Alert>
          )}
        </>
      )}

      {adding && (
        <div className="jt-field-grid">
          <TextField label="ชื่อแคมเปญ" requiredMark value={name} onChange={(e) => setName(e.target.value)} />
          <TextField
            label="ช่วง"
            value={phase}
            onChange={(e) => setPhase(e.target.value)}
            hint="เช่น Early bird — ใช้จัดกลุ่มเวลาเทียบกัน"
          />
          <TextField
            label="งบโฆษณาที่ตั้งไว้"
            type="number"
            min={0}
            value={target}
            onChange={(e) => setTarget(e.target.value)}
          />
          <div className="jt-inline-actions">
            <Button
              size="sm"
              variant="primary"
              disabled={busy === 'new' || !name.trim()}
              onClick={async () => {
                const ok = await state.create({
                  name: name.trim(),
                  phase: phase.trim(),
                  targetAdsSpend: target.trim() === '' ? 0 : Number(target),
                });
                if (ok) { setName(''); setPhase(''); setTarget(''); setAdding(false); }
              }}
            >
              เพิ่มแคมเปญ
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setAdding(false)}>ยกเลิก</Button>
          </div>
        </div>
      )}

      {campaigns.length === 0 && !loading && (
        <p className="jt-field-hint">ยังไม่มีแคมเปญ — เพิ่มแคมเปญแรกเพื่อเริ่มติดตามค่าโฆษณา</p>
      )}

      <ul className="jt-account-list">
        {campaigns.map((c) => (
          /* Named so the row and its actions are one thing to point at, in CSS and in a test. */
          <li key={c.id} className="jt-campaign-row">
            <div className="jt-account">
              <span className="jt-account-title">{c.name}</span>
              <span className="jt-account-sub">
                {c.phase || '—'} · ตั้งงบ {baht(c.targetAdsSpend)} · ใช้จริง{' '}
                {c.actualAdsSpend === null ? 'ยังไม่ได้กรอก' : baht(c.actualAdsSpend)}
                {c.spendSource ? ` · ที่มา ${c.spendSource}` : ''}
              </span>
            </div>
            <div className="jt-inline-actions">
              <Button size="sm" variant="ghost" disabled={busy === c.id} onClick={() => openEditor(c)}>
                บันทึกค่าโฆษณา
              </Button>
              <Button size="sm" variant="ghost" disabled={busy === c.id} onClick={() => setRemoving(c)}>
                ลบ
              </Button>
            </div>
          </li>
        ))}
      </ul>

      {editing && (
        <Card title={`ค่าโฆษณาที่ใช้จริง · ${editing.name}`}>
          <TextField
            label={`ค่าโฆษณาที่ใช้จริงของ ${editing.name}`}
            type="number"
            min={0}
            value={spend}
            onChange={(e) => setSpend(e.target.value)}
            hint="เว้นว่างไว้แปลว่ายังไม่รู้ — ไม่ใช่ ฿0"
          />
          <TextField
            label="ที่มาของตัวเลข"
            value={source}
            onChange={(e) => setSource(e.target.value)}
            hint="เช่น Meta Ads Manager 1–31 ต.ค. — ROAS เชื่อถือได้เท่าที่มาของตัวเลขนี้"
          />
          <div className="jt-inline-actions">
            <Button
              size="sm"
              variant="primary"
              disabled={busy === editing.id}
              onClick={async () => {
                // Empty means unfilled, which is null — and null is not zero here.
                const value = spend.trim() === '' ? null : Number(spend);
                if (await state.setSpend(editing, value, source.trim())) setEditing(null);
              }}
            >
              บันทึก
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>ยกเลิก</Button>
          </div>
        </Card>
      )}

      <ConfirmDialog
        open={removing !== null}
        onClose={() => setRemoving(null)}
        onConfirm={async () => { if (removing && await state.remove(removing)) setRemoving(null); }}
        title={`ลบแคมเปญ ${removing?.name ?? ''}`}
        consequences={[
          `งบที่ตั้งไว้ ${removing ? baht(removing.targetAdsSpend) : ''} จะหายออกจากยอดรวม`,
          removing?.actualAdsSpend === null
            ? 'แคมเปญนี้ยังไม่ได้กรอกค่าโฆษณา — ROAS จะไม่เปลี่ยน'
            : `ค่าโฆษณา ${removing ? baht(removing.actualAdsSpend ?? 0) : ''} จะหายออกจากตัวหาร — ROAS จะสูงขึ้น`,
          'ประวัติการแก้ไขของแคมเปญนี้จะถูกลบไปด้วย',
        ]}
        confirmLabel="ลบแคมเปญ"
        busy={busy === removing?.id}
      />
    </Card>
  );
}
